/* The phase switch's fade, shared by every canvas that replays it — `shared/lib/useSwapReplay`'s
 * CSS half.
 *
 * `opacity .2 → 1` plus `translateY(6px) → 0` on `--ease-fp`: the 07.09 mocks' own
 * `.32s cubic-bezier(.2,.8,.3,1) both`, ramp for ramp. Both the week programator and the sequence
 * mock declare it, byte for byte, on their own grid.
 *
 * ── TWO names for one animation ───────────────────────────────────────────────────────────────
 * A CSS animation replays only when its NAME changes, and stepping from phase 1 to phase 3 has to
 * replay it as surely as stepping to phase 2. That is the mocks' own mechanism
 * (`this.state.tick % 2 ? 'swapA' : 'swapB'`), and `useSwapReplay` is what alternates them.
 *
 * **Neither class is applied on the first render**, which is what keeps a cold entry still: the
 * counter starts at zero and no class is emitted until a switch has actually happened.
 *
 * ── Why it is 420ms long with the ramp in its last 320ms (8 September 2026) ───────────────────
 * The switch read as a stutter, and sampling it at 60ms said exactly why. Stepping the demo plan
 * from a four-training phase to a one-training phase, both movements began together on the commit
 * and ENDED 125ms apart: the fade was past 0.99 opacity 305ms in, while the column's
 * `flex-grow` / `flex-basis` transition — `--dur-grid-flex`, 420ms — was still carrying three
 * columns from 106px to 84px. So the cards went solid and sharp, and then the trainer watched them
 * slide sideways for another eighth of a second under no motion of their own. Two gestures.
 *
 * The fix keeps BOTH of the mocks' durations and moves neither curve: the animation runs the length
 * of the column settle and holds its `from` state through the first 100ms (23.8% of 420ms), so the
 * .32s ramp is unchanged and its END is the column settle's end. The two movements start together,
 * finish together, and read as one — the columns open and the canvas resolves into them.
 *
 * A held keyframe rather than `animation-delay`. Chosen when `tokens/base.css` collapsed only the
 * duration under `prefers-reduced-motion`, and a delay would have left a still viewer looking at 0.2
 * opacity for 100ms before it snapped. The block has zeroed the delay too since 27 September 2026,
 * so either spelling is safe now; the hold stays because it is the measured one, and inside the
 * keyframes it is part of the duration the block collapses.
 *
 * ── Why it is SHARED rather than declared per canvas ─────────────────────────────────────────
 * It lived in `WeekPane.module.css` while the week was the only canvas replaying it. The sequence
 * canvas asks for exactly the same thing, and the reasoning above is the kind that survives being
 * read once and copied wrongly — a second declaration would be the same 24 lines tuned against the
 * same measurement, free to drift. One file, two importers, one `--dur-grid-flex`. */

@keyframes day-swap-a {
  0%,
  23.8% {
    opacity: 0.2;
    transform: translateY(6px);
  }

  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes day-swap-b {
  0%,
  23.8% {
    opacity: 0.2;
    transform: translateY(6px);
  }

  100% {
    opacity: 1;
    transform: none;
  }
}

.swapA,
.swapB {
  animation-duration: var(--dur-grid-flex);
  animation-timing-function: var(--ease-fp);
  animation-fill-mode: both;
}

.swapA {
  animation-name: day-swap-a;
}

.swapB {
  animation-name: day-swap-b;
}
